import React from 'react';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { formatDecimalTime } from '../../utils/formatDecimalTime';
import { CANtrackComment } from 'containers/pages/comments/types/comment';

interface SummaryCardProps {
	showDecimal: boolean;
	timesheetEntries: CANtrackComment[];
	loadingTimesheetEntries: boolean;
}

const TimesheetsSummaryCard: React.FC<SummaryCardProps> = ({
	timesheetEntries,
	showDecimal,
	loadingTimesheetEntries,
}) => {
	const time = timesheetEntries.reduce(
		(acc, entry) => {
			acc.totalTime += entry.timeRegular + entry.timeOver;
			acc.overTime += entry.timeOver;
			acc.regularTime += entry.timeRegular;
			return acc;
		},
		{ totalTime: 0, overTime: 0, regularTime: 0 },
	);
	time.totalTime = parseFloat(time.totalTime.toFixed(2));
	time.overTime = parseFloat(time.overTime.toFixed(2));
	time.regularTime = parseFloat(time.regularTime.toFixed(2));

	// Helper to decide the format based on the `showDecimal` flag
	const formatTime = (time: number) =>
		showDecimal ? `${time.toFixed(2)} hours` : formatDecimalTime(time);

	// Calculate remaining time for the end of day
	const remainingTime = Math.max(8 - time.totalTime, 0);
	const formattedRemainingTime = formatTime(remainingTime);

	if (loadingTimesheetEntries) {
		return (
			<Card>
				<CardContent>
					<Typography variant="body1">
						<Skeleton variant="text" width={'200px'} />
					</Typography>
					<Typography variant="body1">
						<Skeleton variant="text" width={'200px'} />
					</Typography>
					<Typography variant="body1">
						<Skeleton variant="text" width={'200px'} />
					</Typography>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent>
				<Typography variant="body1" gutterBottom>
					Total: {formatTime(time.totalTime)}
				</Typography>
				<Box ml={8}>
					<Typography variant="body2">
						- {formatTime(time.regularTime)} (Regular)
					</Typography>
					<Typography variant="body2">
						- {formatTime(time.overTime)} (Overtime)
					</Typography>
				</Box>
				<Typography variant="caption" color="textSecondary">
					{formattedRemainingTime} remaining till EOD
				</Typography>
			</CardContent>
		</Card>
	);
};

export default TimesheetsSummaryCard;
